// src/components/NominationDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { UserPlus2, Flag, Trash2 } from "lucide-react";
import { apiCall } from "@/api/client";
import { useTeamRiders } from "@/queries/teams";
import HelmetIcon from "@/components/HelmetIcon";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import { isGuestRiderForTeam } from "@/lib/guestRiders";

/**
 * Props:
 * - open: bool
 * - onClose: fn()
 * - match: { id, home_team_id, away_team_id, heats: [...] }
 * - onSaved: fn()
 */

// Hjälpobjekt för hjälmfärger (används i UI för att guida valet av gate och förare)
const HELMET_META = {
  R: { name: "Röd", color: "#DC2626" },
  B: { name: "Blå", color: "#2563EB" },
  G: { name: "Gul", color: "#FACC15" },
  V: { name: "Vit", color: "#FFFFFF", border: "#E4E4E7" },
};

// Gate -> färger för respektive lag (används både i UI och vid sparning)
const GATE_COLOR_PER_TEAM = {
  "1": { home: "R", away: "V" },
  "2": { home: "R", away: "V" },
  "3": { home: "B", away: "G" },
  "4": { home: "B", away: "G" },
};

// Valbara gate-uppsättningar för hemmalaget i nomineringheatet
const HOME_GATE_OPTIONS = [
  {
    id: "13",
    gates: ["1", "3"],
    title: "Spår 1 & 3",
    description: "Hemmalaget väljer startspår 1 och 3",
  },
  {
    id: "24",
    gates: ["2", "4"],
    title: "Spår 2 & 4",
    description: "Hemmalaget väljer startspår 2 och 4",
  },
];

// Små hjälpfunktioner för att visa färgmarkeringar
const renderHelmetSwatch = (letter) => {
  const meta = HELMET_META[letter];
  if (!meta) return null;
  return (
    <HelmetIcon
      color={meta.color}
      className="h-4 w-4"
      title={`Hjälm: ${meta.name}`}
      decorative={false}
    />
  );
};

const GateCard = ({ gate, team, onClick, rider, disabled }) => {
  const colorLetter = GATE_COLOR_PER_TEAM[gate]?.[team];
  const hasRider = Boolean(rider);
  return (
    <button
      type="button"
      onClick={() => onClick?.(gate)}
      disabled={disabled}
      data-selected={hasRider}
      className={`native-v3-nomination-gate w-full border p-3 text-left transition-colors ${
        hasRider
          ? "border-border bg-emerald-50/40 dark:bg-emerald-950/20"
          : "border-border"
      } ${disabled ? "opacity-50" : "hover:border-primary"}`}
    >
      <div className="native-v3-nomination-gate-heading flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground">Spår {gate}</p>
        <div className="flex items-center gap-2">
          {colorLetter && renderHelmetSwatch(colorLetter)}
          {!hasRider ? (
            <UserPlus2 className="h-4 w-4 text-muted-foreground" />
          ) : null}
        </div>
      </div>
      <div className="mt-3 text-sm">
        {rider ? (
          <>
            <div className="native-v3-nomination-selected-rider flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate font-semibold">{rider.name}</p>
                {isGuestRiderForTeam(rider, rider?.context_team_id) ? <GuestRiderBadge className="shrink-0" /> : null}
              </div>
              <Badge
                variant="secondary"
                className="native-v3-nomination-selected-badge border border-emerald-500/40 bg-emerald-500/10 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
              >
                Vald
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{rider.scoreLabel}</p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Ingen förare vald</p>
        )}
      </div>
    </button>
  );
};

const RiderCard = ({
  rider,
  onPick,
  isSelected,
  selectedGateLabel,
  isDisabled,
}) => (
  <button
    type="button"
    onClick={() => onPick?.(rider)}
    disabled={isDisabled}
    data-selected={isSelected}
    className={`native-v3-nomination-rider flex h-full w-full flex-col border p-3 text-left transition ${isDisabled
      ? "opacity-40"
      : isSelected
          ? "border-border bg-emerald-50/40 dark:bg-emerald-950/20"
        : "hover:border-primary"
      }`}
  >
    <div className="native-v3-nomination-rider-heading flex items-center justify-between text-sm font-semibold">
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate">{rider.name}</span>
        {isGuestRiderForTeam(rider, rider?.context_team_id) ? <GuestRiderBadge className="shrink-0" /> : null}
      </span>
      <span className="text-xs font-medium text-muted-foreground">
        {rider.scoreLabel}
      </span>
    </div>
    {rider.is_reserve && (
      <p className="mt-1 text-xs text-muted-foreground">Reserv</p>
    )}
    {isSelected && (
      <p className="native-v3-nomination-rider-selected mt-2 text-xs font-medium">
        Vald till {selectedGateLabel}
      </p>
    )}
  </button>
);

// Själva dialogkomponenten som styr hela flödet för nominering av heat 15
export default function NominationDialog({ open, onClose, match, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTeam, setPickerTeam] = useState(null);
  const [pickerGate, setPickerGate] = useState(null);
  const homeTeamName = match?.home_team || "Hemmalag";
  const awayTeamName = match?.away_team || "Bortalag";
  const { data: homeRidersData, error: homeRidersError } = useTeamRiders(
    match?.home_team_id,
    {
      enabled: open && Boolean(match?.home_team_id),
      includeGuests: true,
      officialMatchId: match?.official_match_id,
    }
  );
  const { data: awayRidersData, error: awayRidersError } = useTeamRiders(
    match?.away_team_id,
    {
      enabled: open && Boolean(match?.away_team_id),
      includeGuests: true,
      officialMatchId: match?.official_match_id,
    }
  );

  const [step, setStep] = useState(0); // 0: gateval, 1: hemmalag, 2: bortalag, 3: sammanställning
  const [homeGateChoice, setHomeGateChoice] = useState(null);
  const [assignments, setAssignments] = useState({ "1": null, "2": null, "3": null, "4": null });
  const riders = useMemo(() => {
    const fromLineup = (entries = []) =>
      entries
        .map((entry) => {
          const lineupNo = Number(entry?.lineup_no);
          return {
            id: String(entry?.rider_id || entry?.id || ""),
            name: entry?.name || "",
            is_reserve: Number.isFinite(lineupNo) ? lineupNo >= 6 : false,
            lineup_no: Number.isFinite(lineupNo) ? lineupNo : null,
            team_id: entry?.team_id || "",
            club_team_id: entry?.club_team_id || entry?.team_id || "",
            guest_active: Boolean(entry?.guest_active),
            guest_for_team_id: entry?.guest_for_team_id || "",
            guest_note: entry?.guest_note || "",
            is_guest_for_team: Boolean(entry?.is_guest_for_team),
          };
        })
        .filter((r) => r.id);

    const toModel = (xs = []) =>
      xs.map((r) => ({
        id: String(r.id),
        name: r.name,
        is_reserve: Boolean(r.is_reserve),
        lineup_no: r.lineup_no ?? null,
        team_id: r.team_id || "",
        club_team_id: r.club_team_id || r.team_id || "",
        guest_active: Boolean(r.guest_active),
        guest_for_team_id: r.guest_for_team_id || "",
        guest_note: r.guest_note || "",
        is_guest_for_team: Boolean(r.is_guest_for_team),
        context_team_id: r.context_team_id || "",
      }));

    const mergeRiders = (rosterEntries = [], lineupEntries = []) => {
      const merged = new Map();
      const rosterIds = new Set();
      for (const rider of rosterEntries) {
        if (!rider?.id) continue;
        rosterIds.add(String(rider.id));
        merged.set(String(rider.id), rider);
      }
      for (const rider of lineupEntries) {
        if (!rider?.id) continue;
        const existing = merged.get(String(rider.id)) || {};
        merged.set(String(rider.id), {
          ...existing,
          ...rider,
          team_id: rider.team_id || existing.team_id || "",
          club_team_id: rider.club_team_id || existing.club_team_id || rider.team_id || existing.team_id || "",
          context_team_id: rider.context_team_id || existing.context_team_id || "",
          guest_active: Boolean(rider.guest_active),
          guest_for_team_id: rider.guest_for_team_id || "",
          guest_note: rider.guest_note || "",
          is_guest_for_team: Boolean(rider.is_guest_for_team),
        });
      }
      return Array.from(merged.values());
    };

    const homeLineup = fromLineup(match?.lineups?.home || []);
    const awayLineup = fromLineup(match?.lineups?.away || []);

    return {
      home: mergeRiders(
        [
          ...toModel(homeRidersData?.mains || []).map((rider) => ({ ...rider, context_team_id: match?.home_team_id || "" })),
          ...toModel(homeRidersData?.reserves || []).map((rider) => ({ ...rider, context_team_id: match?.home_team_id || "" })),
        ],
        homeLineup
      ),
      away: mergeRiders(
        [
          ...toModel(awayRidersData?.mains || []).map((rider) => ({ ...rider, context_team_id: match?.away_team_id || "" })),
          ...toModel(awayRidersData?.reserves || []).map((rider) => ({ ...rider, context_team_id: match?.away_team_id || "" })),
        ],
        awayLineup
      ),
    };
  }, [awayRidersData, homeRidersData, match?.away_team_id, match?.home_team_id, match?.lineups]);
  const rosterLoadError = homeRidersError || awayRidersError;

  // Återställ allt när dialogen stängs för att undvika att tidigare val ligger kvar
  useEffect(() => {
    if (!open) {
      setStep(0);
      setHomeGateChoice(null);
      setAssignments({ "1": null, "2": null, "3": null, "4": null });
      setError("");
      setPickerOpen(false);
      setPickerTeam(null);
      setPickerGate(null);
    }
  }, [open]);

  // Summera individuella poäng + bonus för att kunna visa topplistan
  const totalsByRider = useMemo(() => {
    const acc = {};
    (match?.heats || []).forEach((heat) => {
      (heat.results || []).forEach((res) => {
        const rid = String(res.rider_id || "");
        if (!acc[rid]) {
          acc[rid] = { points: 0, bonus: 0 };
        }
        acc[rid].points += Number(res.points || 0);
        acc[rid].bonus += Number(res.bonus_points || 0);
      });
    });
    return acc;
  }, [match]);

  // Plocka fram lagens tre bästa (inklusive delade placeringar) baserat på total poäng
  const topCandidatesByTeam = useMemo(() => {
    const build = (team) => {
      const mains = (riders[team] || []).filter((r) => !r.is_reserve);
      if (mains.length === 0) return [];
      const enriched = mains
        .map((r) => {
          const totals = totalsByRider[String(r.id)] || { points: 0, bonus: 0 };
          const combined = totals.points + totals.bonus;
          return {
            ...r,
            totals,
            combined,
            scoreLabel: `${totals.points} + ${totals.bonus}`,
          };
        })
        .sort((a, b) => b.combined - a.combined);
      const topThree = enriched.slice(0, 3);
      const threshold = topThree.length ? topThree[topThree.length - 1].combined : 0;
      return enriched.filter((r) => r.combined >= threshold);
    };
    return { home: build("home"), away: build("away") };
  }, [riders, totalsByRider]);

  const homeGates = useMemo(() => {
    if (homeGateChoice === "24") return ["2", "4"];
    if (homeGateChoice === "13") return ["1", "3"];
    return [];
  }, [homeGateChoice]);

  const awayGates = useMemo(() => {
    if (!homeGateChoice) return [];
    const complement = ["1", "2", "3", "4"].filter((g) => !homeGates.includes(g));
    return complement;
  }, [homeGateChoice, homeGates]);
  const clearGate = (gate) => {
    if (!gate) return;
    setAssignments((prev) => ({ ...prev, [gate]: null }));
  };

  const assignRiderToGate = (gate, rider) => {
    if (!gate || !rider) return;
    setAssignments((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((g) => {
        if (updated[g] === rider.id) updated[g] = null;
      });
      updated[gate] = rider.id;
      return updated;
    });
  };


  const selectedRiderForGate = (gate) => {
    const riderId = assignments[gate];
    if (!riderId) return null;
    const combinedTop = [...(topCandidatesByTeam.home || []), ...(topCandidatesByTeam.away || [])];
    const fromTop = combinedTop.find((r) => r.id === riderId);
    if (fromTop) return fromTop;
    const all = [...(riders.home || []), ...(riders.away || [])];
    const fallback = all.find((r) => String(r.id) === String(riderId));
    if (!fallback) return null;
    const totals = totalsByRider[String(fallback.id)] || { points: 0, bonus: 0 };
    return {
      ...fallback,
      scoreLabel: `${totals.points} + ${totals.bonus}`,
    };
  };

  const gateAssignmentsReady = homeGates.every((g) => assignments[g]) && awayGates.every((g) => assignments[g]);

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const payloadAssignments = Object.fromEntries(
        Object.entries(assignments)
          .filter(([gate, riderId]) => gate && riderId)
          .map(([gate, riderId]) => [gate, String(riderId)])
      );
      const body = {
        heat15: {
          assignments: payloadAssignments,
          home_gates: homeGates,
          home: homeGates.map((gate) => payloadAssignments[gate]),
          away: awayGates.map((gate) => payloadAssignments[gate]),
        },
      };
      await apiCall(`/api/matches/${match.id}/nominations`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Kunde inte spara nomineringar");
    } finally {
      setLoading(false);
    }
  };


  const renderGateChoiceStep = () => (
    <section className="native-v3-nomination-step space-y-4">
      <p className="text-sm text-muted-foreground">
        Ange startspår för hemmalagets förare i nomineringsheatet.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {HOME_GATE_OPTIONS.map((option) => {
          const isActive = homeGateChoice === option.id;
          const gates = option.gates;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                if (homeGateChoice !== option.id) {
                  setAssignments({ "1": null, "2": null, "3": null, "4": null });
                }
                setHomeGateChoice(option.id);
                setStep(1);
              }}
              className={`native-v3-nomination-gate-choice border p-4 text-left transition ${isActive ? "border-primary ring-2 ring-primary/40" : "hover:border-primary"
                }`}
              data-selected={isActive}
            >
              <p className="text-sm font-semibold">{option.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
              <div className="mt-3 space-y-2 text-xs">
                {gates.map((gate) => {
                  const letter = GATE_COLOR_PER_TEAM[gate]?.home;
                  return (
                    <div key={gate} className="flex items-center gap-2">
                      {letter && renderHelmetSwatch(letter)}
                      <span>Spår {gate}</span>
                    </div>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );

  const renderRiderSelectionStep = (team) => {
    const gates = team === "home" ? homeGates : awayGates;
    const teamLabel = team === "home" ? homeTeamName : awayTeamName;
    if (!gates.length) return null;

    return (
      <section className="native-v3-nomination-step space-y-4">
        <div className="native-v3-nomination-step-intro flex flex-col gap-1">
          <p className="text-sm font-semibold">{teamLabel}: Välj förare till startspår</p>
          <p className="text-xs text-muted-foreground">
            Tryck på ett gate-kort för att välja förare. Går att ändra senare.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {gates.map((gate) => (
            <div key={gate} className="native-v3-nomination-gate-row grid grid-cols-[1fr_auto] items-stretch gap-2">
              <GateCard
                gate={gate}
                team={team}
                onClick={() => {
                  setPickerTeam(team);
                  setPickerGate(gate);
                  setPickerOpen(true);
                }}
                rider={selectedRiderForGate(gate)}
                disabled={loading}
              />
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="native-v3-nomination-clear h-10 w-10 text-muted-foreground hover:text-foreground"
                  disabled={loading || !assignments[gate]}
                  onClick={() => {
                    clearGate(gate);
                  }}
                  title={`Rensa gate ${gate}`}
                  aria-label={`Rensa gate ${gate}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderSummaryStep = () => (
    <section className="native-v3-nomination-step native-v3-nomination-summary-step space-y-4">
      <p className="text-sm font-semibold">Sammanställning av nominering</p>
      <div className="native-v3-nomination-summary border p-3">
        <p className="mb-2 text-xs font-semibold text-muted-foreground">{homeTeamName}</p>
        <div className="space-y-2 text-sm">
          {homeGates.map((gate) => (
            <div key={`summary-home-${gate}`} className="native-v3-nomination-summary-row flex items-center justify-between">
              <span className="flex items-center gap-2">
                {renderHelmetSwatch(GATE_COLOR_PER_TEAM[gate]?.home)}
                <span>Gate {gate}</span>
              </span>
              <span className="flex items-center gap-2 font-medium">
                {isGuestRiderForTeam(selectedRiderForGate(gate), selectedRiderForGate(gate)?.context_team_id) ? <GuestRiderBadge className="shrink-0" /> : null}
                <span>{selectedRiderForGate(gate)?.name || "-"}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="native-v3-nomination-summary border p-3">
        <p className="mb-2 text-xs font-semibold text-muted-foreground">{awayTeamName}</p>
        <div className="space-y-2 text-sm">
          {awayGates.map((gate) => (
            <div key={`summary-away-${gate}`} className="native-v3-nomination-summary-row flex items-center justify-between">
              <span className="flex items-center gap-2">
                {renderHelmetSwatch(GATE_COLOR_PER_TEAM[gate]?.away)}
                <span>Gate {gate}</span>
              </span>
              <span className="flex items-center gap-2 font-medium">
                {isGuestRiderForTeam(selectedRiderForGate(gate), selectedRiderForGate(gate)?.context_team_id) ? <GuestRiderBadge className="shrink-0" /> : null}
                <span>{selectedRiderForGate(gate)?.name || "-"}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderStepContent = () => {
    if (step === 0 || !homeGateChoice) return renderGateChoiceStep();
    if (step === 1) return renderRiderSelectionStep("home");
    if (step === 2) return renderRiderSelectionStep("away");
    return renderSummaryStep();
  };

  const totalSteps = 4;
  const stepTitle =
    step === 0
      ? "Välj startspår"
      : step === 1
        ? `${homeTeamName}: välj förare till startspår`
        : step === 2
          ? `${awayTeamName}: välj förare till startspår`
          : "Sammanställning";

  const canGoNext =
    (step === 0 && Boolean(homeGateChoice)) ||
    (step === 1 && homeGates.every((g) => Boolean(assignments[g]))) ||
    (step === 2 && awayGates.every((g) => Boolean(assignments[g]))) ||
    (step === 3 && gateAssignmentsReady && Boolean(homeGateChoice));

  const pickerCandidates = pickerTeam ? topCandidatesByTeam[pickerTeam] || [] : [];

  const handlePickRider = (rider) => {
    if (!pickerGate) return;
    if (assignments[pickerGate] === rider.id) {
      clearGate(pickerGate);
      setPickerOpen(false);
      return;
    }
    assignRiderToGate(pickerGate, rider);
    setPickerOpen(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose?.())}>
      <DialogContent 
      className="native-v3-nomination-dialog max-w-3xl max-h-[85dvh] overflow-hidden"
      onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="native-v3-nomination-header">
          <DialogTitle>Nomineringsheat – Heat 15</DialogTitle>
        </DialogHeader>
        <div className="native-v3-nomination-progress mb-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Flag className="h-4 w-4" />
          <Badge variant="secondary">Steg {step + 1} av {totalSteps}</Badge>
          <span>{stepTitle}</span>
        </div>

        {(error || rosterLoadError) && (
          <div className="native-v3-nomination-error mb-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error || rosterLoadError?.message || "Kunde inte ladda trupper"}
          </div>
        )}

        <div className="native-v3-nomination-body max-h-[52dvh] overflow-y-auto pr-1">
          {renderStepContent()}
        </div>

        

        <div className="native-v3-nomination-footer sticky bottom-0 mt-6 flex flex-col gap-3 border-t bg-background pt-4">
          <div className={step > 0 ? "native-v3-nomination-actions native-v3-nomination-actions-three" : "native-v3-nomination-actions"}>
            {step > 0 ? (
              <Button
                variant="ghost"
                className="native-v3-nomination-back"
                onClick={() => setStep((prev) => Math.max(0, prev - 1))}
                disabled={loading}
              >
                Tillbaka
              </Button>
            ) : null}
            <Button className="native-v3-nomination-cancel" variant="outline" onClick={onClose} disabled={loading}>
              Avbryt
            </Button>
            <Button
              className="native-v3-nomination-primary"
              onClick={() => {
                if (step === 0 && homeGateChoice) setStep(1);
                else if (step === 1) setStep(2);
                else if (step === 2) setStep(3);
                else if (step === 3) handleSubmit();
              }}
              disabled={loading || !canGoNext}
            >
              {loading ? "Sparar..." : step === 3 ? "Spara nominering" : "Nästa"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog
      open={pickerOpen}
      onOpenChange={(next) => {
        setPickerOpen(next);
        if (!next) {
          setPickerTeam(null);
          setPickerGate(null);
        }
      }}
    >
      <DialogContent className="native-v3-nomination-picker max-w-lg">
        <DialogHeader className="native-v3-nomination-header">
          <DialogTitle>
            Välj förare {pickerGate ? `för Gate ${pickerGate}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="native-v3-nomination-picker-body max-h-[52vh] overflow-y-auto pr-1">
          <div className="grid gap-3">
            {pickerCandidates.length === 0 ? (
              <div className="native-v3-nomination-empty rounded border border-dashed p-4 text-center text-sm text-muted-foreground">
                Inga förare med registrerade poäng ännu.
              </div>
            ) : (
              pickerCandidates.map((rider) => {
                const assignedGate = Object.keys(assignments).find(
                  (g) => assignments[g] === rider.id
                );
                const isSelected = Boolean(assignedGate);
                const isPickedElsewhere =
                  Boolean(assignedGate) && assignedGate !== pickerGate;
                const isDisabled = loading || isPickedElsewhere;
                return (
                  <RiderCard
                    key={rider.id}
                    rider={rider}
                    onPick={handlePickRider}
                    isSelected={isSelected}
                    selectedGateLabel={assignedGate ? `Gate ${assignedGate}` : ""}
                    isDisabled={isDisabled}
                  />
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}


